import React, { useState, useEffect, useCallback } from 'react';
import { Edit, XCircle, ChevronDown, ChevronUp, Check, X } from 'lucide-react'; // For the edit and close icons
import { QUESTIONDIFFICULTY, QUIZTYPE, TAGLEVELS, defaultPageSize, defaultTotalCount, itemsPerPageOptions, PUBLISH_STATUS_FILTER, QUIZ_TYPE_LABELS, QUIZ_TYPE_COLORS, DIVISION_VALUES, UNIT_VALUES, BOARD_VALUES, isDivisionLevel, isUnitLevel, isBoardLevel } from '../utils/Constants';
import { isEmpty, firstWords } from '../utils/BasicUtilities';
import { formatBdPst } from '../utils/DateUtilities';
import { getAdminQuestionUrl, getAdminQuizGetByIdUrl, getAdminQuizGetUrl, getAdminQuizPutUrl, getAdminQuizDetailsUrl, getAdminQuizPublishUrl, getAdminQuizPublishBulkUrl, getAdminQuizArchiveUrl, getAdminQuizArchiveBulkUrl, getTagUrl, getPublicBcsTermsUrl } from '../utils/UrlUtilities';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import MathText from './ui/MathText';
import YearPicker from './ui/YearPicker';
import { constructQuizPostPayload, constructQuizDetailsPayload, constructQuizPublishPayload, constructQuizPublishBulkPayload, constructQuizArchivePayload, constructQuizArchiveBulkPayload } from '../utils/PayloadUtils';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { parseBucketKey } from '../utils/toeflBlueprint';
import QuizAddManagementPage from './QuizAddForm'
import QuizEditManagementPage from './QuizEditForm'

// Quiz types that support the draft -> publish lifecycle (mirror backend PUBLISHABLE_QUIZ_TYPES).
const PUBLISHABLE_TYPES = ['published', 'compitition', 'micro', 'previous_year'];

export const Input = ({ type, placeholder, value, onChange, className, id, min, max, required, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, disabled, defaultValue }) => (
    <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`form-control ${className || ''}`}
        id={id}
        min={min}
        max={max}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        defaultValue={defaultValue}
    />
);

export const Checkbox = ({ onCheckedChange, checked }) => (
    <input
        type="checkbox"
        className="form-check-input"
        onChange={e => onCheckedChange(e)}
        checked={checked}
    />
);



// Basic Utilities (mocked for standalone)
export const getTotalQuestions = (questionsForQuiz) => {
    let total = 0;
    for (const subject in questionsForQuiz) {
        total += questionsForQuiz[subject].length;
    }
    return total;
};

export function QuestionFilterBar({ onFilter }) {
    const [level, setLevel] = useState('');
    const [difficulty, setDifficulty] = useState('');
    const [search, setSearch] = useState('');
    const [topic, setTopic] = useState('');
    const [subject, setSubject] = useState('');
    const [exam, setExam] = useState('');
    const [division, setDivision] = useState('');
    const [unit, setUnit] = useState('');
    const [year, setYear] = useState('');
    const [bcsTerm, setBcsTerm] = useState('');
    const [random, setRandom] = useState('');
    const [tags, setTags] = useState([]); // All tags from API
    const [bcsTerms, setBcsTerms] = useState([]); // BCS terms for the BCS level
    const CallApi = useApiCaller();

    const handleFilter = () => {
        let filters = {};
        if (!isEmpty(level)) {
            filters["level"] = level;
        }
        if (!isEmpty(difficulty)) {
            filters["difficulty"] = difficulty;
        }
        if (!isEmpty(search)) {
            filters["search"] = search;
        }
        if (!isEmpty(topic)) {
            filters["topic"] = topic;
        }
        if (!isEmpty(subject)) {
            filters["subject"] = subject;
        }
        if (!isEmpty(exam)) {
            filters["exam"] = exam;
        }
        if (!isEmpty(division) && isDivisionLevel(level)) {
            filters["division"] = division;
        }
        if (!isEmpty(unit) && isUnitLevel(level)) {
            filters["unit"] = unit;
        }
        if (!isEmpty(year)) {
            filters["year"] = year;
        }
        if (!isEmpty(bcsTerm)) {
            filters["bcs_term"] = bcsTerm;
        }
        // Random flag (previously mis-assigned to `topic`, which clobbered the topic filter).
        filters["random"] = random === "YES";
        onFilter(filters);
    };

    const handleClearFilter = () => {
        setLevel('');
        setDifficulty('');
        setSearch('');
        setTopic('');
        setSubject('');
        setExam('');
        setDivision('');
        setUnit('');
        setYear('');
        setBcsTerm('');
        setRandom('');
        const filters = {
        };
        onFilter(filters);
    };

    const fetchTags = useCallback(async () => {
        try {
            const tagUrl = getTagUrl();
            const tagGetResponse = await CallApi({
                url: tagUrl,
                method: 'GET',
                token: true,
            });
            if (tagGetResponse.success) {
                setTags(tagGetResponse.data.tags);
            } else {
                setTags([]);
            }
        } catch (error) {
            console.error("Error fetching tags:", error);
            setTags([]);
        }
    }, []);

    useEffect(() => {
        fetchTags();
    }, [level]);

    // Load BCS terms when the BCS level is selected (for the Exam-term filter dropdown).
    useEffect(() => {
        let cancelled = false;
        if (level !== 'BCS') { setBcsTerms([]); return undefined; }
        (async () => {
            const res = await CallApi({ url: getPublicBcsTermsUrl('BCS'), method: 'GET', token: true });
            if (!cancelled && res.success) {
                const data = res.data;
                setBcsTerms(Array.isArray(data) ? data : (data?.terms || data?.bcs_terms || []));
            }
        })();
        return () => { cancelled = true; };
    }, [level, CallApi]);

    return (
        <div className="card shadow-sm mb-4 p-4 kp-filter">
            <h3 className="text-lg font-semibold mb-3">Question Filters</h3>
            <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label text-gray-700">Random</label>
                    <select
                        value={random}
                        onChange={(e) => setRandom(e.target.value)}
                        className="form-select"
                    >
                        <option value="">SELECT</option>
                        <option value="NO">NO</option>
                        <option value="YES">YES</option>
                    </select>
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label text-gray-700">Level <span style={{color: "red"}}>*</span></label>
                    <select
                        value={level}
                        onChange={(e) => {
                            setLevel(e.target.value);
                            setSubject('');
                            setTopic('');
                            setExam('');
                            setDivision('');
                            setUnit('');
                        }}
                        className="form-select"
                    >
                        <option value="">SELECT</option>
                        {TAGLEVELS.map((item) => (
                            item.submenu ? (
                                <optgroup key={item.value} label={item.label}>
                                    {item.submenu.map((sub) => (
                                        <option key={sub.value} value={sub.value}>
                                            {sub.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ) : (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            )
                        ))}
                    </select>
                </div>
                {isDivisionLevel(level) && (
                    <div className="col-12 col-md-6 col-lg-4">
                        <label className="form-label text-gray-700">Division</label>
                        <select value={division} onChange={(e) => setDivision(e.target.value)} className="form-select">
                            <option value="">All Divisions</option>
                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                )}
                {isUnitLevel(level) && (
                    <div className="col-12 col-md-6 col-lg-4">
                        <label className="form-label text-gray-700">Unit</label>
                        <select value={unit} onChange={(e) => setUnit(e.target.value)} className="form-select">
                            <option value="">All Units</option>
                            {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>
                )}
                { level && level.length > 0 && <>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label className="form-label text-gray-700">Subject</label>
                        <select
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="form-select"
                        >
                            <option value="">SELECT</option>
                            {tags
                                .filter(tag => tag.level === level && tag.tag_type === 'SUBJECT' && tag.is_current !== false)
                                .map((tag) => (
                                    <option key={tag.name} value={tag.name}>
                                        {tag.name}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className="col-12 col-md-6 col-lg-4">
                        <label className="form-label text-gray-700">Topic</label>
                        <select
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            className="form-select"
                        >
                            <option value="">SELECT</option>
                            {tags
                                .filter(tag => tag.level === level && tag.tag_type === 'TOPIC' && tag.is_current !== false)
                                .map((tag) => (
                                    <option key={tag.name} value={tag.name}>
                                        {tag.name}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className="col-12 col-md-6 col-lg-4">
                        <label className="form-label text-gray-700">Exam</label>
                        <select
                            value={exam}
                            onChange={(e) => setExam(e.target.value)}
                            className="form-select"
                        >
                            <option value="">SELECT</option>
                            {tags
                                .filter(tag => tag.level === level && tag.tag_type === 'EXAM')
                                .map((tag) => (
                                    <option key={tag.name} value={tag.name}>
                                        {tag.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                </>}

                <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label text-gray-700">Difficulty</label>
                    <select
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value)}
                        className="form-select"
                    >
                        <option value="">All Difficulty</option>
                        {QUESTIONDIFFICULTY.map((diff) => (
                            <option key={diff} value={diff}>
                                {diff}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label text-gray-700">Exam Year</label>
                    <YearPicker
                        value={year}
                        onChange={setYear}
                        placeholder="All Exam Years"
                    />
                </div>

                {level === 'BCS' && (
                    <div className="col-12 col-md-6 col-lg-4">
                        <label className="form-label text-gray-700">BCS Term</label>
                        <select
                            value={bcsTerm}
                            onChange={(e) => setBcsTerm(e.target.value)}
                            className="form-select"
                        >
                            <option value="">All BCS Terms</option>
                            {bcsTerms.map((t) => {
                                const val = typeof t === 'string' ? t : (t.name || t.bcs_term || t.term || '');
                                return val ? <option key={val} value={val}>{val}</option> : null;
                            })}
                        </select>
                    </div>
                )}

                <div className="col-md-12 col-lg-8">
                    <label className="form-label text-gray-700">Search Text</label>
                    <Input
                        type="text"
                        placeholder="Search by question text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full"
                    />
                </div>
            </div>
            <div className="mt-4 d-flex justify-content-end gap-2">
                <button onClick={handleFilter} disabled={!level} className='btn btn-primary'>Apply Filters</button>
                <button onClick={handleClearFilter} className='btn btn-outline-secondary'>Clear Filters</button>
            </div>

        </div>
    );
}

// --- QuestionSelectionGrid Component ---
export function QuestionSelectionGrid({ filters, currentPage, setCurrentPage, onQuestionAssign, selectedQuizSubjects, onEditQuestion }) {
    const [questions, setQuestions] = useState([]);
    // const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const CallApi = useApiCaller();

    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let questionUrl = getAdminQuestionUrl(
                currentPage,
                pageSize,
                filters.random,
                filters.level,
                filters.subject,
                filters.topic,
                filters.exam,
                filters.difficulty,
                filters.search,
                filters.year,
                null,
                { bcsTerm: filters.bcs_term, division: filters.division, unit: filters.unit }
            );

            const questionGetResponse = await CallApi({
                url: questionUrl,
                method: 'GET',
                token: true
            });

            if (questionGetResponse.success) {
                setQuestions(questionGetResponse.data.questions || []);
                setTotalQuestions(questionGetResponse.data.total || 0);
            } else {
                setQuestions([]);
                setTotalQuestions(0);
                setError(questionGetResponse.message || "Failed to fetch questions.");
            }
        } catch (error) {
            console.error('Error fetching questions for grid:', error);
            setQuestions([]);
            setTotalQuestions(0);
            setError("An error occurred while fetching questions.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filters]);

    useEffect(() => {
        fetchQuestions();
    }, [currentPage, pageSize, filters]);

    const totalPages = Math.ceil(totalQuestions / pageSize);

    const handlePageChange = (dir) => {
        setCurrentPage(prev => {
            if (totalPages === 0) return 1;
            if (dir === 'prev') {
                return prev === 1 ? totalPages : prev - 1;
            } else if (dir === 'next') {
                return prev === totalPages ? 1 : prev + 1;
            }
            return prev;
        });
    };

    return (
        <div className="mt-4">
            <h3 className="text-lg font-semibold mb-3">Available Questions</h3>
            {loading && (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-secondary mt-2">Loading questions...</p>
                </div>
            )}
            {error && (
                <div className="alert alert-danger text-center" role="alert">
                    {error}
                </div>
            )}
            {!loading && !error && (
                <>
                    <div className="border rounded-lg overflow-hidden">
                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table w-full border-collapse">
                                <thead className="bg-gray-100 sticky top-0">
                                    <tr>
                                        <th className="p-2 border text-start w-3/6 text-uppercase">Question Text</th>
                                        <th className="p-2 border text-start w-1/6 text-uppercase">Topic</th>
                                        <th className="p-2 border text-start w-1/6 text-uppercase">Subjects</th>
                                        <th className="p-2 border text-center w-1/6 text-uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {questions.length > 0 ? (
                                        questions.map(question => (
                                            <tr key={question.question_id} className="border-b hover:bg-gray-50">
                                                <td className="p-2 text-start w-3/6" style={onEditQuestion ? { cursor: 'pointer' } : undefined} onClick={onEditQuestion ? () => onEditQuestion(question) : undefined} title={onEditQuestion ? 'Click to edit this question' : `${question.question_text || ''}${question.side_text ? `\n\nPassage: ${question.side_text}` : ''}`}>
                                                    <div>{firstWords(question.question_text, 10)}{question.group_id ? <span className="badge bg-info-subtle text-info ms-2" title="Part of a passage/audio group — the whole group is added to the test together">{question.group_size ? `group of ${question.group_size}` : 'group'}</span> : null}</div>
                                                    {question.side_text ? <div className="small text-muted">Passage: {firstWords(question.side_text, 10)}</div> : null}
                                                    {question.audio_url ? <div className="small text-muted">🔊 audio</div> : null}
                                                </td>
                                                <td className="p-2 text-start w-1/6">
                                                    {question.topic ? <span className="badge bg-primary-subtle text-primary rounded-pill px-2 py-1">{question.topic}</span> : <span className="text-muted">—</span>}
                                                </td>
                                                <td className="p-2 text-start w-1/6"> {/* New Column Data */}
                                                    {question.subject && question.subject.split(',').map((sub, index) => (
                                                        <span
                                                            key={index}
                                                            className="badge bg-secondary-subtle text-secondary rounded-pill px-2 py-1 me-1 mb-1 cursor-pointer"
                                                            onClick={() => onQuestionAssign(question, sub.trim())} // Direct assignment on badge click
                                                            title={`Click to add this question to ${sub.trim()}`}
                                                        >
                                                            {sub.trim()}
                                                        </span>
                                                    ))}
                                                </td>
                                                <td className="p-2 text-center w-1/6">
                                                    {(() => {
                                                        // TOEFL section questions (subject = Reading/Listening/Writing/Speaking) can only
                                                        // go into a module of their own section; other quizzes keep all subject options.
                                                        const toeflNames = ['Reading', 'Listening', 'Writing', 'Speaking'];
                                                        const isToeflQ = toeflNames.includes(question.subject);
                                                        const opts = isToeflQ
                                                            ? selectedQuizSubjects.filter(s => parseBucketKey(s).sectionName === question.subject)
                                                            : selectedQuizSubjects;
                                                        return (
                                                    <select
                                                        onChange={(e) => {
                                                            onQuestionAssign(question, e.target.value);
                                                        }}
                                                        value="" // Always default to empty for new selections
                                                        className="form-select form-select-sm w-auto"
                                                        disabled={opts.length === 0}
                                                    >
                                                        <option value="" disabled>{isToeflQ ? 'Add to Module' : 'Add to Subject'}</option>
                                                        {opts.length > 0 ? (
                                                            opts.map(sub => (
                                                                <option key={sub} value={sub}>
                                                                    {sub}
                                                                </option>
                                                            ))
                                                        ) : (
                                                            <option value="" disabled>{isToeflQ ? 'No matching module' : 'No subjects selected'}</option>
                                                        )}
                                                    </select>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="p-4 text-center text-gray-500">No questions found with current filters.</td> {/* Updated colspan */}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {/* Pagination Footer */}
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="pageSizeSelectGrid" className="form-label text-secondary mb-0">Items per page:</label>
                            <select
                                id="pageSizeSelectGrid"
                                className="form-select form-select-sm w-auto"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                            >
                                {itemsPerPageOptions.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="btn btn-outline-secondary rounded-pill"
                                onClick={() => handlePageChange('prev')}
                            >
                                Previous
                            </button>
                            <span className="text-secondary">Page {currentPage} of {totalPages === 0 ? 1 : totalPages} · Total: {totalQuestions}</span>
                            <button
                                className="btn btn-outline-secondary rounded-pill"
                                onClick={() => handlePageChange('next')}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// --- SubjectQuestionGrid Component ---
export function SubjectQuestionGrid({ subjectName, assignedQuestions, onRemoveQuestion, onEditQuestion }) {
    const [isCollapsed, setIsCollapsed] = useState(false); // State to manage collapse/expand

    return (
        <div className="mb-4 p-3 border rounded-lg shadow-sm bg-gray-50 my-2">
            <div className="d-flex justify-content-between align-items-center mb-2"> {/* Flexbox for alignment */}
                <h4 className="text-md font-semibold mb-0"> {/* mb-0 to remove default margin */}
                    {subjectName} Questions ({assignedQuestions.length})
                </h4>
                <button
                    className="btn btn-link p-0 text-dark" // Minimal styling for the button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    aria-expanded={!isCollapsed}
                    aria-controls={`collapse-questions-${subjectName.replace(/\s+/g, '-')}`} // Unique ID for accessibility
                    title={isCollapsed ? "Expand questions" : "Collapse questions"}
                >
                    {isCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </button>
            </div>
            {!isCollapsed && ( // Conditionally render content based on isCollapsed
                assignedQuestions.length > 0 ? (
                    <ul id={`collapse-questions-${subjectName.replace(/\s+/g, '-')}`} className="space-y-2">
                        {assignedQuestions.map(question => (
                            <li key={question.question_id} className="flex items-center justify-between bg-white p-2 rounded-md border">
                                <span
                                    className="text-sm flex-grow me-2"
                                    style={onEditQuestion ? { cursor: 'pointer' } : undefined}
                                    onClick={onEditQuestion ? () => onEditQuestion(question) : undefined}
                                    title={onEditQuestion ? 'Click to edit this question' : `${question.question_text || ''}${question.side_text ? `\n\nPassage: ${question.side_text}` : ''}`}
                                >
                                    {firstWords(question.question_text, 10)}
                                    {question.group_id ? <span className="badge bg-info-subtle text-info ms-2">passage</span> : null}
                                    {question.topic ? <span className="badge bg-primary-subtle text-primary ms-2">{question.topic}</span> : null}
                                    {question.side_text ? <span className="d-block small text-muted">Passage: {firstWords(question.side_text, 10)}</span> : null}
                                </span>
                                <XCircle className="text-danger cursor-pointer" style={{ width: '1.5rem', height: '1.5rem' }} onClick={(e) => { e.stopPropagation(); onRemoveQuestion(question.question_id, subjectName); }}/>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-500">No questions assigned to this subject yet.</p>
                )
            )}
        </div>
    );
}



const QuizFilterBar = ({onApplyFilter, onClearFilter}) => { // Renamed onFilter to onApplyFilter
    const [pendingLevel, setPendingLevel] = useState('');
    const [pendingCreatedBy, setPendingCreatedBy] = useState('');
    const [pendingQuizType, setPendingQuizType] = useState('');
    const [pendingIsPublished, setPendingIsPublished] = useState('');
    const [pendingPublishedSince, setPendingPublishedSince] = useState('');
    const [pendingIsArchived, setPendingIsArchived] = useState(false);
    const [pendingDivision, setPendingDivision] = useState('');
    const [pendingUnit, setPendingUnit] = useState('');
    const [pendingBoard, setPendingBoard] = useState('');

    const handleApply = () => {
        let filters = {};
        if (!isEmpty(pendingLevel)) {
            filters["level"] = pendingLevel;
        }
        if (!isEmpty(pendingDivision)) {
            filters["division"] = pendingDivision;
        }
        if (!isEmpty(pendingUnit)) {
            filters["unit"] = pendingUnit;
        }
        if (!isEmpty(pendingBoard)) {
            filters["board"] = pendingBoard;
        }
        if (!isEmpty(pendingCreatedBy)) {
            filters["createdBy"] = pendingCreatedBy;
        }
        if (!isEmpty(pendingQuizType)) {
            filters["quizType"] = pendingQuizType;
        }
        if (!isEmpty(pendingIsPublished)) {
            filters["isPublished"] = pendingIsPublished;
        }
        if (!isEmpty(pendingPublishedSince)) {
            // <input type="date"> gives YYYY-MM-DD; backend parses it as a datetime.
            filters["publishedSince"] = pendingPublishedSince;
        }
        // Archived is a tri-state at the API (false hides, true shows only archived).
        filters["isArchived"] = pendingIsArchived ? "true" : "false";
        onApplyFilter(filters); // Use onApplyFilter
    };
    const handleClear = () => {
        setPendingLevel('');
        setPendingCreatedBy('');
        setPendingQuizType('');
        setPendingIsPublished('');
        setPendingPublishedSince('');
        setPendingIsArchived(false);
        setPendingDivision('');
        setPendingUnit('');
        setPendingBoard('');
        onClearFilter(); // Use onClearFilter
    };

    return (
        <div className="card shadow-sm mb-4 kp-filter">
            <div className="card-body">
                <h5 className="card-title text-dark mb-3">Test Filters</h5>
                <div className="row g-3 align-items-end">
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterLevel" className="form-label text-gray-700">Level </label>
                        <select
                            id="filterLevel"
                            value={pendingLevel}
                            onChange={(e) => { setPendingLevel(e.target.value); setPendingDivision(''); setPendingUnit(''); setPendingBoard(''); }}
                            className="form-select"
                        >
                            <option value={""}>
                                {"All Levels"}
                            </option>
                            {TAGLEVELS.map((item) => (
                                item.submenu ? (
                                    <optgroup key={item.value} label={item.label}>
                                        {item.submenu.map((sub) => (
                                            <option key={sub.value} value={sub.value}>
                                                {sub.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ) : (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                )
                            ))}
                        </select>
                    </div>

                    {/* Division / Unit / Board — coupled to level, only when applicable. */}
                    {isDivisionLevel(pendingLevel) && (
                        <div className="col-6 col-md-4">
                            <label className="form-label text-gray-700">Division</label>
                            <select className="form-select" value={pendingDivision} onChange={(e) => setPendingDivision(e.target.value)}>
                                <option value="">All Divisions</option>
                                {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                    )}
                    {isUnitLevel(pendingLevel) && (
                        <div className="col-6 col-md-4">
                            <label className="form-label text-gray-700">Unit</label>
                            <select className="form-select" value={pendingUnit} onChange={(e) => setPendingUnit(e.target.value)}>
                                <option value="">All Units</option>
                                {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                    )}
                    {isBoardLevel(pendingLevel) && (
                        <div className="col-6 col-md-4">
                            <label className="form-label text-gray-700">Board</label>
                            <select className="form-select" value={pendingBoard} onChange={(e) => setPendingBoard(e.target.value)}>
                                <option value="">All Boards</option>
                                {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="col-6 col-md-4">
                        <label htmlFor="filterCreatedBy" className="form-label text-gray-700">Created By</label>
                        <Input type="text" id="filterCreatedBy" placeholder="Email" value={pendingCreatedBy} onChange={(e) => {setPendingCreatedBy(e.target.value)}} className="w-full" />
                    </div>
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterQuizType" className="form-label text-gray-700">Test Type</label>
                        <select
                            id="filterQuizType"
                            value={pendingQuizType}
                            onChange={(e) => setPendingQuizType(e.target.value)}
                            className="form-select"
                        >
                            <option value={""}>
                                {"All Type"}
                            </option>
                            {QUIZTYPE.map((qType) => (
                            <option key={qType} value={qType}>
                                {QUIZ_TYPE_LABELS[qType] || qType}
                            </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterIsPublished" className="form-label text-gray-700">Publish Status</label>
                        <select
                            id="filterIsPublished"
                            value={pendingIsPublished}
                            onChange={(e) => setPendingIsPublished(e.target.value)}
                            className="form-select"
                        >
                            {PUBLISH_STATUS_FILTER.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-6 col-md-4">
                        <label htmlFor="filterPublishedSince" className="form-label text-gray-700">Published Since</label>
                        <Input type="date" id="filterPublishedSince" value={pendingPublishedSince} onChange={(e) => setPendingPublishedSince(e.target.value)} className="w-full" />
                    </div>
                    <div className="col-6 col-md-4 d-flex align-items-center">
                        <div className="form-check mt-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="filterIsArchived"
                                checked={pendingIsArchived}
                                onChange={(e) => setPendingIsArchived(e.target.checked)}
                            />
                            <label className="form-check-label text-gray-700" htmlFor="filterIsArchived">
                                Show archived only
                            </label>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button onClick={handleApply} className="btn btn-primary">
                        Apply Filters
                    </button>
                    <button onClick={handleClear} className="btn btn-outline-secondary">
                        Clear Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

// Inline-editable text cell for the Manage Tests grid (Title / Description). Shows the value with a
// small pencil; clicking it swaps in an input/textarea with Save (✓) / Cancel (✗). Enter saves a
// single-line field; Esc always cancels. `onSave(newValue)` returns a promise<bool> — true collapses
// the editor, false keeps it open so the admin can retry. `allowEmpty=false` blocks a blank title.
function EditableTextCell({ value, onSave, multiline = false, allowEmpty = true, placeholder, extra }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);

    const commit = async () => {
        const trimmed = draft.trim();
        if (!allowEmpty && trimmed === '') return; // keep editing — a title can't be blank
        if (trimmed === (value ?? '').trim()) { setEditing(false); return; } // no change
        setSaving(true);
        const ok = await onSave(trimmed);
        setSaving(false);
        if (ok) setEditing(false);
    };
    const cancel = () => { setDraft(value ?? ''); setEditing(false); };

    if (!editing) {
        return (
            <div className="d-flex align-items-start gap-1">
                <span className="flex-grow-1" style={{ whiteSpace: 'pre-wrap' }}>{value || '-'}{extra}</span>
                <button
                    type="button"
                    className="btn btn-link p-0 text-secondary flex-shrink-0"
                    onClick={() => setEditing(true)}
                    title="Edit"
                >
                    <Edit size={14} />
                </button>
            </div>
        );
    }

    return (
        <div>
            {multiline ? (
                <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={draft}
                    placeholder={placeholder}
                    autoFocus
                    disabled={saving}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
                />
            ) : (
                <input
                    type="text"
                    className="form-control form-control-sm"
                    value={draft}
                    placeholder={placeholder}
                    autoFocus
                    disabled={saving}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commit(); else if (e.key === 'Escape') cancel(); }}
                />
            )}
            <div className="d-flex gap-1 mt-1">
                <button type="button" className="btn btn-sm btn-success py-0 px-1" onClick={commit} disabled={saving} title="Save">
                    <Check size={14} />
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={cancel} disabled={saving} title="Cancel">
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}

function QuizGrid({ filters, currentPage, setCurrentPage, setShowAddQuizForm, onEditQuiz }) { // Removed unused props for clarity
    const [quizes, setQuizes] = useState([]);
    const [inGridSearchTerm, setInGridSearchTerm] = useState('');
    // Debounced copy of the "Search in Grid" box, sent to the server so the search spans the WHOLE
    // catalog (all pages), not just the loaded page.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    // const [currentPage, setCurrentPage] = useState(1);
    const [filteredTotalQuizCount, setFilteredTotalQuizCount] = useState(defaultTotalCount);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [catalogWarning, setCatalogWarning] = useState('');
    const CallApi = useApiCaller();

    const fetchQuizes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // if (!isEmpty(filters)) {
            //     setCurrentPage(1);
            // }
            let currentFilters = {
                ...filters,
                page: currentPage,
                limit: pageSize,
            };
            const quizUrl = getAdminQuizGetUrl(
                currentFilters.page,
                currentFilters.limit,
                currentFilters.level,
                currentFilters.createdBy,
                currentFilters.quizType,
                currentFilters.isPublished,
                currentFilters.publishedSince,
                currentFilters.isArchived,
                { board: currentFilters.board, division: currentFilters.division, unit: currentFilters.unit, search: debouncedSearch }
            );
            const quizGetResponse = await CallApi({
                url: quizUrl,
                method: 'GET',
                token: true,
            });

            if (quizGetResponse.success) {
                setQuizes(quizGetResponse.data.quizes || []);
                setFilteredTotalQuizCount(quizGetResponse.data.total || 0);
                setCatalogWarning(quizGetResponse.data.warning || '');
            } else {
                setQuizes([]);
                setFilteredTotalQuizCount(defaultTotalCount);
                setError(quizGetResponse.message || "Failed to fetch tests.");
            }
        } catch (error) {
            setQuizes([]);
            setFilteredTotalQuizCount(defaultTotalCount);
            setError("An error occurred while fetching tests.");
            console.error('Fetch Quizes Error:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filters, debouncedSearch]);

    useEffect(() => {

        fetchQuizes();
    }, [currentPage, pageSize, filters, debouncedSearch]);

    // Debounce the "Search in Grid" box → server search, resetting to page 1 on a new term.
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(inGridSearchTerm.trim());
            setCurrentPage(1);
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inGridSearchTerm]);


    const displayedQuizes = quizes;

    const totalPages = Math.ceil(filteredTotalQuizCount / pageSize);

    // Inline title/description edit from a grid row — narrow PATCH that leaves every other field
    // (questions, metadata, competition window, publish state) untouched. Updates the row in place
    // so the grid scroll/page stay put. Returns true on success so the editor can collapse.
    const handleSaveDetails = async (quiz, { title, description }) => {
        try {
            const response = await CallApi({
                url: getAdminQuizDetailsUrl(quiz.quiz_id),
                method: 'PATCH',
                payload: constructQuizDetailsPayload(title, description),
                token: true,
            });
            if (response.success) {
                setQuizes(prev => prev.map(q => q.quiz_id === quiz.quiz_id
                    ? { ...q, title: response.data?.title ?? title, description: response.data?.description ?? description }
                    : q));
                return true;
            }
            setError(response.message || 'Failed to update test details.');
            return false;
        } catch (error) {
            setError('An error occurred while updating test details.');
            console.error('Update Test Details Error:', error);
            return false;
        }
    };

    const handleTogglePublish = async (quiz) => {
        try {
            const nextPublished = !quiz.is_published;
            const response = await CallApi({
                url: getAdminQuizPublishUrl(quiz.quiz_id),
                method: 'POST',
                payload: constructQuizPublishPayload(nextPublished),
                token: true,
            });
            if (response.success) {
                // Update the row IN PLACE (flip the Publish/Unpublish button + status badge) rather
                // than refetching — keeps the grid scroll position and page exactly where it was.
                setQuizes(prev => prev.map(q => q.quiz_id === quiz.quiz_id
                    ? { ...q, is_published: nextPublished, published_at: (response.data && response.data.published_at) ?? (nextPublished ? q.published_at : null) }
                    : q));
            } else {
                setError(response.message || 'Failed to update publish status.');
            }
        } catch (error) {
            setError('An error occurred while updating publish status.');
            console.error('Publish Quiz Error:', error);
        }
    };

    const handleBulkPublish = async (isPublished) => {
        if (!selectedRows || selectedRows.length === 0) return;
        try {
            const response = await CallApi({
                url: getAdminQuizPublishBulkUrl(),
                method: 'POST',
                payload: constructQuizPublishBulkPayload(selectedRows, isPublished),
                token: true,
            });
            if (response.success) {
                await fetchQuizes();
                setSelectedRows([]);
            } else {
                setError(response.message || 'Failed to update publish status.');
            }
        } catch (error) {
            setError('An error occurred while updating publish status.');
            console.error('Bulk Publish Error:', error);
        }
    };

    const handleToggleArchive = async (quiz) => {
        try {
            const response = await CallApi({
                url: getAdminQuizArchiveUrl(quiz.quiz_id),
                method: 'POST',
                payload: constructQuizArchivePayload(!quiz.is_archived),
                token: true,
            });
            if (response.success) {
                await fetchQuizes();
            } else {
                setError(response.message || 'Failed to update archive status.');
            }
        } catch (error) {
            setError('An error occurred while updating archive status.');
            console.error('Archive Quiz Error:', error);
        }
    };

    const handleBulkArchive = async () => {
        if (!selectedRows || selectedRows.length === 0) return;
        setDeleting(true);
        setError(null);
        try {
            const response = await CallApi({
                url: getAdminQuizArchiveBulkUrl(),
                method: 'POST',
                payload: constructQuizArchiveBulkPayload(selectedRows, true),
                token: true,
            });
            if (response.success) {
                await fetchQuizes();
                setSelectedRows([]);
            } else {
                setError(response.message || 'Failed to archive tests.');
            }
        } catch (error) {
            setError('An error occurred while archiving the tests.');
            console.error('Bulk Archive Error:', error);
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };

    const handleEdit = async (quiz) => {
        const editingQuizId = quiz.quiz_id;
        if (quiz) {
            setLoading(true);
            setError(null);
            try {
                const adminQuizGetByIdUrl = getAdminQuizGetByIdUrl(editingQuizId, 1);
                const getByIdQuizResponse = await CallApi({
                    url: adminQuizGetByIdUrl,
                    method: 'GET',
                    token: true,
                });

                if (getByIdQuizResponse?.success) {
                    if (onEditQuiz) {
                        onEditQuiz(getByIdQuizResponse.data);
                    }
                } else {
                    setError(getByIdQuizResponse.message || 'Failed to fetch test details for editing.');
                }
            } catch (error) {
                setError('An error occurred while fetching test details for editing.');
                console.error('Edit Quiz Error:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCheckboxChange = (id, event) => {
        setSelectedRows(prev => {
            if (event.target.checked) {
                return [...prev, id];
            } else {
                return prev.filter(rowId => rowId !== id);
            }
        });
    };

    const handleSelectAllChange = (event) => {
        if (event.target.checked) {
            setSelectedRows(displayedQuizes.map(quiz => quiz.quiz_id));
        } else {
            setSelectedRows([]);
        }
    };

    const handlePageChange = (dir) => {
        setCurrentPage(prev => {
            if (totalPages === 0) return 1;
            if (dir === 'prev') {
                return prev === 1 ? totalPages : prev - 1; // Corrected: prev - 1
            } else if (dir === 'next') {
                return prev === totalPages ? 1 : prev + 1; // Corrected: prev + 1
            }
            return prev;
        });
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1); // Reset to first page on page size change
    };

    const handleAdd = () => {
        setShowAddQuizForm(true);
    };

    return (
        <div className="container-fluid py-4">
            {catalogWarning && (
                <div className="alert alert-warning" role="alert">
                    ⚠️ {catalogWarning}
                </div>
            )}
            {/* Search in Grid and Action bar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center flex-grow-1 me-3">
                    <label htmlFor="inGridSearch" className="form-label text-nowrap me-2 mb-0">Search in Grid:</label>
                    <Input
                        type="text"
                        id="inGridSearch"
                        placeholder="Search all tests by title/description…"
                        value={inGridSearchTerm}
                        onChange={(e) => setInGridSearchTerm(e.target.value)}
                        className="flex-grow-1"
                    />
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-success" onClick={handleAdd}>Add Test</button>
                    <button className="btn btn-outline-success" onClick={() => handleBulkPublish(true)} disabled={selectedRows.length === 0}>Publish ({selectedRows.length})</button>
                    <button className="btn btn-outline-warning" onClick={() => handleBulkPublish(false)} disabled={selectedRows.length === 0}>Unpublish ({selectedRows.length})</button>
                    <button className="btn btn-danger" onClick={() => setConfirmOpen(true)} disabled={selectedRows.length === 0}>Archive Selected ({selectedRows.length})</button>
                </div>
            </div>

            {/* Loading/Error State */}
            {loading && (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-secondary mt-2">Loading tests...</p>
                </div>
            )}
            {error && (
                <div className="alert alert-danger text-center" role="alert">
                    {error}
                </div>
            )}

            {/* Quizzes Table */}
            {!loading && !error && (
                <div className="card shadow-sm overflow-hidden">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="bg-gray-200 text-dark">
                                <tr>
                                    <th scope="col" style={{ width: '4%', minWidth: '40px' }} className="p-2 text-center text-uppercase">
                                        <Checkbox
                                            onCheckedChange={handleSelectAllChange}
                                            checked={selectedRows.length > 0 && displayedQuizes.length > 0 && selectedRows.every(id => displayedQuizes.some(quiz => quiz.quiz_id === id))}
                                        />
                                    </th>
                                    <th scope="col" style={{ width: '18%', minWidth: '150px' }} className="p-2 text-start text-uppercase">Title</th>
                                    <th scope="col" style={{ width: '15%', minWidth: '250px' }} className="p-2 text-start text-uppercase">Description</th>
                                    <th scope="col" style={{ width: '8%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Type</th>
                                    <th scope="col" style={{ width: '10%', minWidth: '130px' }} className="p-2 text-start text-uppercase">Status</th>
                                    <th scope="col" style={{ width: '8%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Level</th>
                                    <th scope="col" style={{ width: '8%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Div / Unit</th>
                                    <th scope="col" style={{ width: '10%', minWidth: '150px' }} className="p-2 text-center text-uppercase">Actions</th>
                                    <th scope="col" style={{ width: '13%', minWidth: '120px' }} className="p-2 text-start text-uppercase">Start Time</th>
                                    <th scope="col" style={{ width: '13%', minWidth: '120px' }} className="p-2 text-start text-uppercase">End Time</th>
                                    <th scope="col" style={{ width: '13%', minWidth: '120px' }} className="p-2 text-start text-uppercase">Published At</th>
                                    <th scope="col" style={{ width: '8%', minWidth: '100px' }} className="p-2 text-start text-uppercase">Duration (min)</th>
                                    <th scope="col" style={{ width: '6%', minWidth: '80px' }} className="p-2 text-center text-uppercase">Questions</th>
                                    <th scope="col" style={{ width: '10%', minWidth: '120px' }} className="p-2 text-start text-uppercase">Created By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedQuizes.length === 0 ? (
                                    <tr>
                                        <td colSpan="13" className="text-center text-muted py-3">No tests found matching your criteria.</td>
                                    </tr>
                                ) : (
                                    displayedQuizes.map(quiz => {
                                        const isPublishable = PUBLISHABLE_TYPES.includes(quiz.quiz_type);
                                        return (
                                        <tr key={quiz.quiz_id} className={quiz.is_archived ? 'opacity-50' : ''}>
                                            <td style={{ width: '4%' }} className="p-2 text-center">
                                                <Checkbox
                                                    onCheckedChange={(e) => handleCheckboxChange(quiz.quiz_id, e)}
                                                    checked={selectedRows.includes(quiz.quiz_id)}
                                                />
                                            </td>
                                            <td style={{ width: '15%' }} className="p-2 text-start">
                                                <EditableTextCell
                                                    value={quiz.title}
                                                    allowEmpty={false}
                                                    placeholder="Test title"
                                                    onSave={(v) => handleSaveDetails(quiz, { title: v, description: quiz.description })}
                                                    extra={quiz.metadata?.source_ingest_job && (
                                                        <span className="badge bg-info-subtle text-info ms-1" title={`Built from ingest job #${quiz.metadata.source_ingest_job} — review & publish`}>From ingest</span>
                                                    )}
                                                />
                                            </td>
                                            <td style={{ width: '25%' }} className="p-2 text-start">
                                                <EditableTextCell
                                                    value={quiz.description}
                                                    multiline
                                                    placeholder="Test description"
                                                    onSave={(v) => handleSaveDetails(quiz, { title: quiz.title, description: v })}
                                                />
                                            </td>
                                            <td style={{ width: '8%' }} className="p-2 text-start">
                                                <span className={`badge bg-${QUIZ_TYPE_COLORS[quiz.quiz_type] || 'secondary'}-subtle text-black rounded-pill px-2 py-1`}>
                                                    {QUIZ_TYPE_LABELS[quiz.quiz_type] || quiz.quiz_type}
                                                </span>
                                            </td>
                                            <td style={{ width: '10%' }} className="p-2 text-start">
                                                {!isPublishable ? (
                                                    <span className="badge bg-secondary-subtle text-secondary rounded-pill px-2 py-1">N/A</span>
                                                ) : quiz.is_published ? (
                                                    <span className="badge bg-success-subtle text-black rounded-pill px-2 py-1" title={quiz.published_at ? `Published ${formatBdPst(quiz.published_at)}` : 'Published'}>
                                                        Published
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-warning-subtle text-black rounded-pill px-2 py-1">Draft</span>
                                                )}
                                                {quiz.is_archived && (
                                                    <span className="badge bg-danger-subtle text-black rounded-pill px-2 py-1 ms-1">Archived</span>
                                                )}
                                            </td>
                                            <td style={{ width: '8%' }} className="p-2 text-start">{quiz.level}</td>
                                            <td style={{ width: '8%' }} className="p-2 text-start">
                                                {quiz.division || (quiz.unit ? `Unit ${quiz.unit}` : <span className="text-muted">—</span>)}
                                            </td>

                                            <td style={{ width: '10%' }} className="p-2 text-center">
                                                <div className="d-flex justify-content-center align-items-center gap-1">
                                                    <button
                                                        className="btn btn-link p-0 text-primary"
                                                        onClick={() => handleEdit(quiz)}
                                                        title="Edit Test"
                                                    >
                                                        <Edit size={20} />
                                                    </button>
                                                    {isPublishable && (
                                                        <button
                                                            className={`btn btn-sm ${quiz.is_published ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                            onClick={() => handleTogglePublish(quiz)}
                                                            title={quiz.is_published ? 'Unpublish' : 'Publish'}
                                                        >
                                                            {quiz.is_published ? 'Unpublish' : 'Publish'}
                                                        </button>
                                                    )}
                                                    <button
                                                        className={`btn btn-sm ${quiz.is_archived ? 'btn-outline-secondary' : 'btn-outline-danger'}`}
                                                        onClick={() => handleToggleArchive(quiz)}
                                                        title={quiz.is_archived ? 'Unarchive' : 'Archive'}
                                                    >
                                                        {quiz.is_archived ? 'Unarchive' : 'Archive'}
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ width: '10%' }} className="p-2 text-start">{quiz.start_time ? formatBdPst(quiz.start_time) : '-'}</td>
                                            <td style={{ width: '10%' }} className="p-2 text-start">
                                                {quiz.end_time ? formatBdPst(quiz.end_time) : '-'}
                                                {quiz.quiz_type === 'compitition' && quiz.end_time && (
                                                    new Date(quiz.end_time) < new Date()
                                                        ? <span className="badge bg-secondary-subtle text-secondary rounded-pill ms-1">Ended</span>
                                                        : (quiz.is_published ? <span className="badge bg-success-subtle text-success rounded-pill ms-1">Open</span> : null)
                                                )}
                                            </td>
                                            <td style={{ width: '10%' }} className="p-2 text-start">{quiz.published_at ? formatBdPst(quiz.published_at) : '-'}</td>
                                            <td style={{ width: '8%' }} className="p-2 text-start">{quiz.duration_minutes}</td>
                                            <td style={{ width: '6%' }} className="p-2 text-center">
                                                <span className="badge bg-primary-subtle text-primary rounded-pill">{quiz.total_questions ?? 0}</span>
                                            </td>
                                            <td style={{ width: '10%' }} className="p-2 text-start">{quiz.created_by}</td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination Footer */}
            <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="d-flex align-items-center gap-2">
                    <label htmlFor="pageSizeSelect" className="form-label text-secondary mb-0">Items per page:</label>
                    <select
                        id="pageSizeSelect"
                        className="form-select form-select-sm w-auto"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        {itemsPerPageOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-outline-secondary rounded-pill"
                        onClick={() => handlePageChange('prev')}
                    >
                        Previous
                    </button>
                    <span className="text-secondary">Page {currentPage} of {totalPages === 0 ? 1 : totalPages} · Total: {filteredTotalQuizCount}</span>
                    <button
                        className="btn btn-outline-secondary rounded-pill"
                        onClick={() => handlePageChange('next')}
                    >
                        Next
                    </button>
                </div>
            </div>
            <ConfirmDeleteDialog
                open={confirmOpen}
                title="Archive tests"
                message={`Archive ${selectedRows.length} test(s)? They will be hidden from users but their submissions and statistics are preserved. You can unarchive them later.`}
                onConfirm={handleBulkArchive}
                onCancel={() => setConfirmOpen(false)}
                busy={deleting}
            />
        </div>
    );
}

function QuizManagement() {
    const [appliedFilters, setAppliedFilters] = useState({
        level: "",
        createdBy: "",
        quizType: "",
        isPublished: "",
        publishedSince: "",
        isArchived: "false",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddQuizForm, setShowAddQuizForm] = useState(false);
    const [showEditQuizForm, setShowEditQuizForm] = useState(false);
    const [editingQuiz, setEditingQuiz] = useState(null);

    const handleApplyFilters = (newFilters) => {
        setCurrentPage(1);
        setAppliedFilters(newFilters);
    };
    const handleClearFilters = () => {
        setCurrentPage(1);
        setAppliedFilters({ level: "", createdBy: "", quizType: "", isPublished: "", publishedSince: "", isArchived: "false" });
    };

    const handleAddFormCancel = () => setShowAddQuizForm(false);
    const handleAddFormSave = (newQuizData) => {
        console.log("Quiz added:", newQuizData);
        setShowAddQuizForm(false);
    };

    const handleEditFormCancel = () => {
        setShowEditQuizForm(false);
        setEditingQuiz(null);
    };
    const handleEditFormSave = (updatedQuizData) => {
        console.log("Quiz updated:", updatedQuizData);
        setShowEditQuizForm(false);
        setEditingQuiz(null);
    };

    const handleEditQuizFromGrid = useCallback((quiz) => {
        setEditingQuiz(quiz);
        setShowEditQuizForm(true);
    }, []);

    return (
        <div className={`p-4 ${ (showAddQuizForm || showEditQuizForm) ? 'position-relative' : ''}`}>
            {showAddQuizForm ? (
                <QuizAddManagementPage
                    onCancel={handleAddFormCancel}
                    onSave={handleAddFormSave}
                />
            ) : showEditQuizForm ? (
                <QuizEditManagementPage
                    quiz={editingQuiz}
                    onCancel={handleEditFormCancel}
                    onSave={handleEditFormSave}
                />
            ) : (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h1 className="fs-2 fw-bold text-dark mb-0">Manage Tests</h1>
                    </div>
                    <QuizFilterBar
                        onApplyFilter={handleApplyFilters}
                        onClearFilter={handleClearFilters}
                    />
                    <QuizGrid
                        filters={appliedFilters}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        showAddQuizForm={showAddQuizForm}
                        setShowAddQuizForm={setShowAddQuizForm}
                        showEditQuizForm={showEditQuizForm}
                        setShowEditQuizForm={setShowEditQuizForm}
                        onEditQuiz={handleEditQuizFromGrid}
                    />
                </>
            )}

            <style>
                {`
                .content-overlay {
                    filter: blur(2px) grayscale(50%);
                    pointer-events: none;
                    opacity: 0.7;
                    transition: filter 0.3s ease, opacity 0.3s ease;
                }
                .modal.d-block {
                    z-index: 1050;
                }
                .table thead th {
                    position: sticky;
                    top: 0;
                    z-index: 1;
                    background-color: #e2e8f0;
                    color: #2d3748;
                    border-right: none !important;
                }
                .table tbody tr td {
                    border-right: none !important;
                    padding-left: 0.75rem;
                    padding-right: 0.75rem;
                }
                .table tbody tr {
                    border-bottom: 1px solid #dee2e6;
                }
                .table-hover tbody tr:hover {
                    background-color: #f5f5f5;
                }

                .badge.bg-info-subtle { background-color: #cfe2ff; color: #084298; }
                .badge.bg-success-subtle { background-color: #d1e7dd; color: #0f5132; }
                .badge.bg-danger-subtle { background-color: #f8d7da; color: #842029; }
                .badge.bg-secondary-subtle { background-color: #e2e3e5; color: #41464b; }
                .badge.bg-warning-subtle { background-color: #fff3cd; color: #664d03; }
                .badge.bg-primary-subtle { background-color: #cfe2ff; color: #084298; }
                `}
            </style>
        </div>
    );
}

export default QuizManagement;
